/**
 * Read-only modal preview of assessment events scheduled on a company cycle.
 */
import React, { useEffect } from "react";
import { translateUi } from "../i18n/index";
import { Calendar, Clock, X } from "../icons";
import { GbrModalBackdrop } from "../GbrModalBackdrop";
import { cb, cbCn } from "./cycleBuilderTheme";
import { formatCycleDurationLabel, resolveDisplayedCycleMonths } from "./formatCycleDuration";

export interface CycleEventPreviewRow {
  id?: string;
  event_name: string;
  offset_months_open: number;
  offset_months_close: number;
  sort_order?: number;
}

function monthLabel(m: number): string {
  return m === 0 ? translateUi("Month 0 (from start)") : `Month ${m}`;
}

function windowLabel(open: number, close: number): string {
  if (open === close) {
    return translateUi("Opens and closes in {{month}}", { month: monthLabel(open) });
  }
  return translateUi("{{start}} to {{end}}", { start: monthLabel(open), end: monthLabel(close) });
}

export function AssessmentEventsPreviewModal({
  open,
  cycleTitle,
  expiryYears,
  expiryMonths,
  events,
  isDarkMode = false,
  canEdit = false,
  onClose,
  onEdit,
}: {
  open: boolean;
  cycleTitle: string;
  expiryYears?: number;
  expiryMonths?: number | null;
  events: CycleEventPreviewRow[];
  isDarkMode?: boolean;
  /** Draft cycle — show edit action in footer. */
  canEdit?: boolean;
  onClose: () => void;
  onEdit?: () => void;
}) {
  useEffect(() => {
    if (!open) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!open) return null;

  const duration = formatCycleDurationLabel(resolveDisplayedCycleMonths({ expiry_years: expiryYears, expiry_months: expiryMonths }));
  const sorted = [...events].sort(
    (a, b) => Number(a.sort_order ?? 0) - Number(b.sort_order ?? 0) || Number(a.offset_months_open) - Number(b.offset_months_open)
  );

  return (
    <div
      className="fixed inset-0 z-[80] flex items-end justify-center bg-slate-950/60 p-0 backdrop-blur-sm sm:items-center sm:p-4"
      onClick={onClose}
      role="presentation"
    >
      <div
        className={cbCn(
          "relative flex max-h-[min(92vh,720px)] w-full flex-col overflow-hidden rounded-t-2xl border shadow-2xl sm:max-w-2xl sm:rounded-2xl",
          isDarkMode ? "border-slate-700 bg-slate-900" : "border-slate-200 bg-white"
        )}
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="events-preview-title"
      >
        <GbrModalBackdrop opacity={isDarkMode ? 0.06 : 0.09} />
        <header
          className={cbCn(
            "relative shrink-0 border-b px-4 py-4 sm:px-6",
            isDarkMode ? "border-slate-700 bg-slate-900/95" : "border-slate-100 bg-slate-50/95"
          )}
        >
          <div className="flex items-start gap-3 pr-10">
            <span
              className={cbCn(
                "flex h-11 w-11 shrink-0 items-center justify-center rounded-xl",
                isDarkMode ? "bg-amber-500/20 text-amber-200" : "bg-amber-100 text-amber-800"
              )}
              aria-hidden
            >
              <Calendar className="h-5 w-5" />
            </span>
            <div className="min-w-0 flex-1">
              <p className={cbCn("text-[11px] font-semibold uppercase tracking-wider", isDarkMode ? "text-amber-300" : "text-amber-800")}>
                {translateUi("Assessment events")}
              </p>
              <h2
                id="events-preview-title"
                className={cbCn("mt-0.5 text-base font-semibold leading-snug break-words sm:text-lg", isDarkMode ? "text-slate-50" : "text-slate-900")}
              >
                {cycleTitle}
              </h2>
              <div className="mt-2 flex flex-wrap gap-1.5">
                <span
                  className={cbCn(
                    "inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-medium",
                    isDarkMode ? "bg-slate-800 text-slate-300" : "bg-white text-slate-600 ring-1 ring-slate-200"
                  )}
                >
                  <Clock className="h-3 w-3" aria-hidden />
                  {duration}
                </span>
                <span className={cbCn("rounded-full px-2 py-0.5 text-[11px] font-medium", isDarkMode ? "bg-slate-800 text-slate-300" : "bg-white text-slate-600 ring-1 ring-slate-200")}>
                  {sorted.length} {sorted.length === 1 ? translateUi("event") : translateUi("events")}
                </span>
              </div>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className={cbCn(
              "absolute right-3 top-3 rounded-xl p-2 transition-colors",
              isDarkMode ? "text-slate-400 hover:bg-slate-800 hover:text-slate-100" : "text-slate-500 hover:bg-slate-100 hover:text-slate-800"
            )}
            aria-label={translateUi("Close")}
          >
            <X className="h-5 w-5" />
          </button>
        </header>

        <div className="relative min-h-0 flex-1 overflow-y-auto px-4 py-4 sm:px-6">
          {sorted.length === 0 ? (
            <p className={cbCn("py-8 text-center text-sm", cb.bodySm(isDarkMode))}>{translateUi("No assessment events configured yet.")}</p>
          ) : (
            <ol className="space-y-3">
              {sorted.map((ev, index) => (
                <li
                  key={ev.id ?? `${ev.event_name}-${index}`}
                  className={cbCn(
                    "rounded-xl border px-4 py-3",
                    isDarkMode ? "border-slate-600 bg-slate-800/50" : "border-slate-200 bg-slate-50/80"
                  )}
                >
                  <div className="flex items-start gap-3">
                    <span
                      className={cbCn(
                        "flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-xs font-bold tabular-nums",
                        isDarkMode ? "bg-indigo-500/25 text-indigo-200" : "bg-indigo-100 text-indigo-800"
                      )}
                    >
                      {index + 1}
                    </span>
                    <div className="min-w-0 flex-1">
                      <p className={cbCn("font-semibold", isDarkMode ? "text-slate-100" : "text-slate-900")}>{ev.event_name}</p>
                      <p className={cbCn("mt-1 text-sm", cb.bodySm(isDarkMode))}>
                        {windowLabel(Number(ev.offset_months_open), Number(ev.offset_months_close))}
                      </p>
                    </div>
                  </div>
                </li>
              ))}
            </ol>
          )}
        </div>

        <footer
          className={cbCn(
            "relative flex shrink-0 flex-col gap-2 border-t px-4 py-3 sm:flex-row sm:items-center sm:justify-between sm:px-6",
            isDarkMode ? "border-slate-700 bg-slate-900/95" : "border-slate-100 bg-slate-50/95"
          )}
        >
          <p className={cbCn("text-xs", cb.bodySm(isDarkMode))}>
            {translateUi("These windows apply from each employee's cycle start date when the cycle is assigned.")}
          </p>
          <div className="flex shrink-0 justify-end gap-2">
            {canEdit && onEdit ? (
              <button type="button" onClick={onEdit} className={cbCn("rounded-lg px-4 py-2 text-sm font-semibold", cb.btnSecondary(isDarkMode))}>
                {translateUi("Edit events")}
              </button>
            ) : null}
            <button type="button" onClick={onClose} className={cbCn("rounded-lg px-4 py-2 text-sm font-semibold", cb.btnPrimary(false))}>
              {translateUi("Close")}
            </button>
          </div>
        </footer>
      </div>
    </div>
  );
}
